import React, { FC, memo, useMemo } from 'react';
import { EventMappingRpcResponseItem } from '@om-fe/api';
import { MatchScoreLayout, MatchScoreScopes } from '@om-fe/widgets/dist/widgets/match-score/match-score.constants';
import { WidgetType } from '@om-fe/widgets/dist/components/external-sport-widgets/widget.constants';
import { MatchScoreboard } from '@om-fe/widgets/dist/components/match-scoreboard/match-scoreboard.tsx';
import { MatchDetailsSliderWidgetIds } from '@om-fe/widgets/dist/widgets/match-details-slider/match-details-slider.constants';
import { EventTimeModel, MatchDetailsViewModel, DisciplineLiveStatus } from '@om-fe/models';
import { MatchDetailsSliderContainer } from '../match-details-slider-container/match-details-slider-container';
import { BemMethod, createBem } from '@om-fe/utils';
import getConfig from '@om-fe/core/utils/config';
import { useBetradar } from '@om-fe/widgets/dist/components/betradar/betradar.hooks';
import { refId } from '../../common/constants';

const bem: BemMethod = createBem('MatchDetailsHeaderContent');

enum SidebarTabsType {
    SidebarTabsEvent = 'SidebarTabsEvent',
    MatchDetailsTabsLiveEvent = 'MatchDetailsTabsLiveEvent',
}

interface MatchDetailsHeaderContentPublicProps {
    readonly scoreWidgetId: EventMappingRpcResponseItem | null;
    readonly hasBetradarWidget: boolean;
    readonly streamingWidget?: EventMappingRpcResponseItem | null;
    readonly disciplineId: string;
    readonly clientId: string | undefined;
    readonly event?: MatchDetailsViewModel | null;
    readonly eventTime?: EventTimeModel | null;
    readonly liveStatus: DisciplineLiveStatus;
    readonly matchId: string;
    readonly handleOnBetradarTrack?: (param1: string, param2: unknown) => void;
    readonly sidebarShow?: boolean;
    readonly onTabChanged?: (active: MatchDetailsSliderWidgetIds) => void;
    readonly isAuthenForTopic?: boolean;
}

export const MatchDetailsHeaderContent: FC<MatchDetailsHeaderContentPublicProps> = memo(function MatchDetailsHeaderContentComponent(
    {
        scoreWidgetId,
        hasBetradarWidget,
        streamingWidget,
        matchId,
        disciplineId,
        event,
        eventTime,
        liveStatus,
        sidebarShow,
        onTabChanged,
        isAuthenForTopic,
    }: MatchDetailsHeaderContentPublicProps
) {
    const { betradar, statScoreWidgetV2Config } = getConfig();
    const clientId: string | undefined = betradar?.clientId;

    const [isLoadingBrWidget, hasBetradarLicenseError] = useBetradar({
        matchId: scoreWidgetId?.value,
        betradarId: clientId,
    }, `#${refId}`);
    
    const tabsList: MatchDetailsSliderWidgetIds[] = useMemo(() => {
        return (
            (!hasBetradarLicenseError && (scoreWidgetId?.key === WidgetType.BrWidget || scoreWidgetId?.key === WidgetType.BrWidgetLMT))
            ?
                (liveStatus == DisciplineLiveStatus.NOT_LIVE)
                ?
                    (sidebarShow)
                    ?
                        betradar?.enabledRightSideBarLMT
                            ?
                                [
                                    MatchDetailsSliderWidgetIds.LiveMatchTracker,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                            :
                                [
                                    MatchDetailsSliderWidgetIds.Statistics,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                    : betradar?.enabledRightSideBarLMT
                        ?
                            [
                                MatchDetailsSliderWidgetIds.MatchDetailsHeader,
                                MatchDetailsSliderWidgetIds.LiveMatchTracker,
                                MatchDetailsSliderWidgetIds.LiveTV,
                            ]
                        : statScoreWidgetV2Config?.group
                            ?
                                [
                                    MatchDetailsSliderWidgetIds.LiveMatchTracker,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                            :
                                [
                                    MatchDetailsSliderWidgetIds.ScoreBetradarWidget,
                                    MatchDetailsSliderWidgetIds.Statistics,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                : (sidebarShow)
                    ?
                        betradar?.enabledRightSideBarLMT
                            ?
                                [
                                    MatchDetailsSliderWidgetIds.LiveMatchTracker,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                            :
                                [
                                    MatchDetailsSliderWidgetIds.Statistics,
                                    MatchDetailsSliderWidgetIds.Visualization,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                    : betradar?.enabledRightSideBarLMT
                        ?
                            [
                                MatchDetailsSliderWidgetIds.ScoreMatchScoreboard,
                                MatchDetailsSliderWidgetIds.LiveMatchTracker,
                                MatchDetailsSliderWidgetIds.LiveTV,
                            ]
                        : statScoreWidgetV2Config?.group
                            ?
                                [
                                    MatchDetailsSliderWidgetIds.LiveMatchTracker,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
                            :
                                [
                                    MatchDetailsSliderWidgetIds.ScoreBetradarWidget,
                                    MatchDetailsSliderWidgetIds.MatchDescriptionBetradarWidget,
                                    MatchDetailsSliderWidgetIds.Statistics,
                                    MatchDetailsSliderWidgetIds.LiveTV,
                                ]
            :
                (sidebarShow)
                ?
                [
                    MatchDetailsSliderWidgetIds.Visualization,
                    MatchDetailsSliderWidgetIds.LiveTV,
                ]
                :
                [
                    MatchDetailsSliderWidgetIds.ScoreMatchScoreboard,
                    MatchDetailsSliderWidgetIds.MatchDescriptionOmScoreWidget,
                    MatchDetailsSliderWidgetIds.LiveTV,
                ]
        );
    }, [hasBetradarLicenseError, scoreWidgetId]);

    if ((!scoreWidgetId || (hasBetradarWidget && hasBetradarLicenseError)) && !streamingWidget && event && eventTime && !sidebarShow) {
        return (
            <MatchScoreboard
                scopeName={MatchScoreScopes.Large}
                matchId={matchId}
                scoreLayout={MatchScoreLayout.V1}
                disciplineId={disciplineId}
                event={event}
                eventTime={eventTime}
                isAuthenForTopic={isAuthenForTopic}
            />
        );
    }

    return (
        <>
            {((!isLoadingBrWidget && hasBetradarWidget) || scoreWidgetId || streamingWidget) 
                ? <MatchDetailsSliderContainer
                    matchId={matchId}
                    tabsList={tabsList}
                    disciplineId={disciplineId}
                    className={(sidebarShow) ? SidebarTabsType.SidebarTabsEvent : SidebarTabsType.MatchDetailsTabsLiveEvent}
                    onTabChanged={onTabChanged}
                    isLive={liveStatus === DisciplineLiveStatus.LIVE}
                    isRightSidebar={sidebarShow}
                />
                : <div id={refId} className={bem('BetradarWidgetError')}/>
            }
        </>
    )
});
